import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { ILanguageExtended } from "@multiweb/types/contracts";
import { CancellationTokenSource } from "Global/Base/Promises/CancellationTokenSource";
import { CalendarDataRow } from "UI/UI.Controls/DataGridSubcontrols/Components/CalendarDataRow";
import { IOpenFormMenuEvent } from "UI/UI.Controls/DataGridSubcontrols/Components/IHeaderRow";
import { DropDownMenuTwoStateItem } from "UI/UI.Controls/DropDownMenu/DropDownMenuTwoStateItem";
import { setExtendedPropertiesOnElement } from "UI/utils";
import { IEditableDataSource } from "../../../../Global/Base/DataSources/Interfaces";
import { InvalidOperationException } from "../../../../Global/Base/Exceptions";
import { EMPTY_ITEM_RIGHTS_COLLECTION } from "../../../../Global/Base/Rights/RightTypes";
import { EnumUtils } from "../../../../Global/Base/Utils/EnumUtils";
import { TimerUtils } from "../../../../Global/Base/Utils/TimerUtils";
import { EventsController } from "../../../../Global/Dom/EventsController";
import { AttrValueReader } from "../../../../OW.Remoting/Remoting/AttrValueReader";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { OWManager } from "../../../../OW.Remoting/Remoting/OWManager";
import { ActionWithData } from "../../../UI.Controls/Actions/ActionWithData";
import { ActionContext, ActionType, IAction, IGraphDataForAction, IMultiObjectAction, ITwoStateAction, SelectionType } from "../../../UI.Controls/Actions/IAction";
import { MenuType } from "../../../UI.Controls/Actions/MenuType";
import { IDataRowElement } from "../../../UI.Controls/DataGridSubcontrols/Components/IDataRow";
import { DropDownMenu } from "../../../UI.Controls/DropDownMenu/DropDownMenu";
import { DropDownMenuItem } from "../../../UI.Controls/DropDownMenu/DropDownMenuItem";
import { DropDownMenuWithSubmenuItem } from "../../../UI.Controls/DropDownMenu/DropDownMenuWithSubmenuItem";
import { IListDataGrid } from "../../../UI.Controls/IDataGrid";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { GenericControl } from "../Controls/Control";
import { IHtmlElementWithResources } from "../Controls/ControlInterfaces";
import { LocalizedStringBuilder } from "../DataBuilders/LocalizedStringBuilder";
import { MessageButtonType, MessageType, MessageWindow } from "../MessageWindow";
import { ActionsContainer } from "../Toolbar/ActionsContainer";
import { FulltextSearchControl } from "../Toolbar/FulltextSearchControl";
import { ToolbarGroup } from "../Toolbar/ToolbarGroup";
import { UIManager } from "../UIManager";
import { MultiObjectAction } from "../XGControls/Actions/MultiObjectAction";
import { NodeAction, NodeActionType } from "../XGControls/Actions/NodeAction";
import { StandardAction } from "../XGControls/Actions/StandardAction";
import { StandardRecordAction } from "../XGControls/Actions/StandardRecordAction";
import { DesktopToolbarButton } from "../XGControls/Actions/ToolbarButton";
import { XGAction } from "../XGControls/Actions/XGAction";
import { DropDownMenuButtonDisplayOn, DropDownMenuItemWithButton } from "../XGControls/Filters/DropDownMenuItems/DropDownMenuItemWithButton";
import { CalendarFrame } from "../XGControls/Frames/CalendarFrame";
import { IMasterableFrame, IMasterableFrameWithDataGrid, IObjectListEdit } from "../XGControls/Frames/FrameInterfaces";
import { FrameSharedMethods } from "../XGControls/Frames/FrameSharedMethods";
import { GenericListFrame } from "../XGControls/Frames/GenericListFrame";
import { IDetailFrame, IDetailMasterableFrame } from "../XGControls/Frames/IDetailMasterableFrame";
import { IFrame, IFrameWithRights } from "../XGControls/IFrame";
import { FormUICustomProperty, Icon, IFormUI, IMenu, MenuTypes } from "./FormUI";
import { MenuUIExtensionProperties, ToolbarActionPrescription } from "./FormUIShared";
import { BtnCaption, FormUIStandard } from "./FormUIStandard";
import { ObjectActionsMenu } from "./ObjectActionsMenu";

const logger = LazyLoadingSectionLogger.create(LoggingSection.formUI);

export class MenuUIStandard implements IMenu {
    private _eventsController = new EventsController();
    private _isDisposed = false;

    protected _masterFormUI: IFormUI;
    protected _objectActionsMenu: ObjectActionsMenu;
    protected _frameActionsWaitingToAdd: Array<ToolbarActionPrescription> = new Array<ToolbarActionPrescription>();
    protected _objectActionsWaitingToAdd: Array<ToolbarActionPrescription> = new Array<ToolbarActionPrescription>();
    protected _actionContainer: ActionsContainer;
    protected _filterContainer: HTMLElement;
    private __loaderElement: HTMLElement;
    private _frameElement: HTMLElement;

    private _fulltextContainer: HTMLDivElement;
    protected _allActions: Set<IAction> = new Set<IAction>();
    protected _objectActions: Set<IAction> = new Set<IAction>();
    protected _frameActions: Set<IAction> = new Set<IAction>();
    protected _frameMenuActions: Set<IAction> = new Set<IAction>();
    protected _multiObjectActionsMap: Map<IAction, Set<IMultiObjectAction>> = new Map<IAction, Set<IMultiObjectAction>>();
    protected _frame: IFrame;
    /** Označuje viditelnost nastavovanou z vnějšku komponenty. Skutečná viditelnost se skládá z hidetoolbar, této hodnoty a informace, zda je některé z toolbarových akcí viditelná. */
    protected _outerVisibility: boolean = false;
    private _btnSaveChanges: DesktopToolbarButton; //reference na tlačítko uložit změny
    private _itemsInEditMode: Set<string> = new Set(); //pamatuje si které záznamy byly rozeditovány akcí "editovat", aby při opětovném otevření řádkového menu akcí mohla nabídnou akce pro uložení a zrušení změn.
    // @ts-ignore // kvůli nečtení
    private _fulltextSupported: boolean = false;
    public usesSingleToolbar: boolean = false;
    private _frameMenu: DropDownMenu;
    private _frameMenuCancellationToken: CancellationTokenSource;
    // menu loader
    private _showMenuLoaderTimeout: number;
    private _menuLoaderTimeout: number;
    private _menuLoaderCanHide: boolean = false;
    private _menuLoaderCounter: number = 0;
    protected _pendingRightsEvaluationObjId: string | undefined;
    private __getLanguages: () => Promise<readonly ILanguageExtended[]>;
    private _actionContainerResizeHandler: () => void;
    protected get _getLanguages(): () => Promise<readonly ILanguageExtended[]> {
        return this.__getLanguages;
    }

    // Doba do které když se načtou data tak se loader nezobrazí
    private get menuLoaderDelay(): number {
        return 300;
    }
    // Po zobrazení bude loader zobrazen minimálně 1800ms (je to délka animace)
    private get menuLoaderMinLength(): number {
        return 1800;
    }
    private _actionTypeComparator = (actionType: ActionType) => (action: IAction) => !actionType || action.actionType === actionType;
    private _actionContextComparator = (context: ActionContext) => (action: IAction) => action.context === context;

    protected get _loaderElement(): HTMLElement {
        return this.__loaderElement;
    }

    /**
     * Html reperzentace menu
     */
    private _element = document.createElement("div"); //samotne menu
    public get element(): HTMLElement {
        return this._element;
    }

    public get objectActions(): IAction[] {
        return Array.from(this._objectActionsMenu.getActions());
    }

    private get _hideToolbar(): boolean {
        if (this._frame instanceof IDetailMasterableFrame && this._frame.detail && this._frame.ownerForm === this._frame.detail.ownerForm) {
            return this._frame.detail.hideToolBar || this._frame.hideToolBar;
        } else {
            if (this._frame)
                return this._frame.hideToolBar;
            else
                return false;
        }
    }

    constructor(formUI: IFormUI, frameElement: HTMLElement, public isVisible = false, ctrlID: string, frame: IFrame | undefined, getLanguages: () => Promise<readonly ILanguageExtended[]>) {
        this._masterFormUI = formUI;
        this.__getLanguages = getLanguages;

        this.element.classList.add("DesktopMenu", "standardMenu", "hiddenToolbar");

        this._attachToolbar(frameElement, frame);

        const actionContainerElem = document.createElement("div");

        actionContainerElem.classList.add("actionContainer");
        this._objectActionsMenu = new ObjectActionsMenu(getLanguages);
        this._frame = frame;
        this._frameElement = frameElement;

        this.element.appendChild(actionContainerElem);
        this._actionContainer = new ActionsContainer({
            element: actionContainerElem,
            originalRightsDefinition: EMPTY_ITEM_RIGHTS_COLLECTION,
            ctrlID: ctrlID != null ? ctrlID : "-default",
        });

        this.setVisibility(isVisible);
        this._bindListeners(frame);

        this._actionContainerResizeHandler = () => this._actionContainer.groups.throttledResizeGroups();
        UIManager.instance.resizeObserve(frameElement, this._actionContainerResizeHandler);

        this.element.insertAdjacentElement("afterend", this.__loaderElement = this._createLoaderElement());

        this._filterContainer = document.createElement("div");
        this._filterContainer.classList.add("filter-container");
        this.element.appendChild(this._filterContainer);
    }

    protected _attachToolbar(frameElement: HTMLElement, _frame?: IFrame): void {
        frameElement.insertAdjacentElement("afterbegin", this.element);
    }

    private _createLoaderElement(): HTMLElement {
        const loaderElement = createElement({ type: "div", classes: "loader menu-loader hiddenLoader" });
        loaderElement.appendChild(createElement({ type: "div", classes: "bar" }));

        return loaderElement;
    }

    private *_getObjectAndFrameActionsIterator(): IterableIterator<IAction> {
        for (const objectAction of this._objectActions) {
            yield objectAction;
        }
        for (const frameAction of this._frameActions) {
            yield frameAction;
        }
    }

    public showMenuLoader(): void {
        this._menuLoaderCounter++;
        if (this._loaderElement.classList.contains("loading")) {
            return;
        }
        if (this._showMenuLoaderTimeout) {
            clearInterval(this._showMenuLoaderTimeout);
        }
        this._showMenuLoaderTimeout = self.setTimeout(() => {
            if (!this._loaderElement.classList.contains("loading")) {
                this._loaderElement.classList.add("loading");
            }
            this._showMenuLoaderTimeout = null;

            this._menuLoaderTimeout = self.setTimeout(() => {
                this._menuLoaderCanHide = true;
                this._menuLoaderTimeout = null;
                if (this._menuLoaderCounter === 0) {
                    this.hideMenuLoader();
                }
            }, this.menuLoaderMinLength); //délka animace loaderu
        }, this.menuLoaderDelay);
    }

    public hideMenuLoader(): void {
        if (this._showMenuLoaderTimeout) {
            clearInterval(this._showMenuLoaderTimeout);
            clearInterval(this._menuLoaderTimeout);
            if (this._loaderElement.classList.contains("loading")) {
                this._loaderElement.classList.remove("loading");
            }
            this._showMenuLoaderTimeout = null;
            this._menuLoaderCounter = 0;
            return;
        }
        if (this._menuLoaderCounter > 1) {
            this._menuLoaderCounter--;
            return;
        }
        if (this._menuLoaderCanHide && !this._menuLoaderTimeout) {
            this._loaderElement.classList.remove("loading");
            this._menuLoaderTimeout = null;
            this._menuLoaderCanHide = false;
        }
        this._menuLoaderCounter = 0;
    }

    /** Na základě události zjistí jestli se má vykreslit kontextové menu vyhodnoceného objektu */
    protected _checkContextMenuReady(_frame: IFrame, dataKey: string): void {
        if (this._pendingRightsEvaluationObjId && dataKey && this._pendingRightsEvaluationObjId == dataKey) {
            this._openThreeDotsOnSelectedRow(dataKey, undefined, undefined, true);
            this._pendingRightsEvaluationObjId = undefined;
        }
    }
    private _bindListeners(frame?: IFrame): void {
        if (!frame) {
            return;
        }
        if (frame instanceof IFrameWithRights) {
            this._eventsController.add(frame.contextActionsReady, (sender, obj) => {
                this._updateVisibility();
                this._checkContextMenuReady(sender, obj);
            });
        }

        let detail = frame instanceof IDetailFrame ? frame : undefined;
        if (frame instanceof IDetailMasterableFrame) {
            if (frame.detail instanceof IDetailFrame)
                detail = frame.detail;
            this._eventsController.add(frame.changesCanceled, (_sender) => this._itemsInEditMode.clear());
            this._eventsController.add(frame.recordChangesCanceled, (_sender, dataKey) => this._itemsInEditMode.delete(dataKey));
            this._eventsController.add(frame.actualObjectSaved, (_sender, obj) => {
                if (obj)
                    this._itemsInEditMode.delete(obj.id);
            });
            this._eventsController.add(frame.editingRow, (_sender, obj) => {
                if (obj)
                    this._itemsInEditMode.add(obj.id);
            });
        }

        if (detail) {
            this._eventsController.add(detail.editingRow, (_sender, obj) => {
                if (obj)
                    this._itemsInEditMode.add(obj.id);
            });
            this._eventsController.add(detail.changesCanceled, (_sender) => this._itemsInEditMode.clear());
        }

        if (frame instanceof GenericListFrame && frame.dataSource) {
            //při esMulti je možné uložit jen jeden objekt, pak je potřeba aktualizovat counter pro operaci SaveAll
            if (frame.dataSource instanceof IEditableDataSource) {
                this._eventsController.add(frame.dataSource.afterSaved, (sender, _v) => this._updateChangedRecordsCounter(sender.editedObjectsCount));
            }

            //pokud se změní atribut nějakého objektu, tak se aktualizuje counter pro operaci SaveAll
            this._eventsController.add(frame.dataSource.attributesChanged, (sender, v) => {
                if (sender instanceof IEditableDataSource) {
                    this._updateChangedRecordsCounter(sender.editedObjectsCount);
                    this._updateChangedRow(frame.currentDataGrid.dataRows.find(r => r.dataKey == v.key), v.isChanged);
                }
            });

            this._eventsController.add(frame.actualObjectSaved, (_sender, v) => {
                if (v) {
                    this._updateChangedRow(frame.currentDataGrid.dataRows.find(r => r.dataKey == v.id), v.isChanged);
                }
            });

            this._eventsController.add(frame.dataSource.changesCancelled, () => {
                this._updateChangedRecordsCounter(0);
            });

            // Zahození změn (smazání) pouze u jednoho záznamu
            this._eventsController.add(frame.dataSource.deleted, (sender, _) => {
                if (sender instanceof IEditableDataSource) {
                    this._updateChangedRecordsCounter(sender.editedObjectsCount - 1);
                }
            });
        }
        this._bindListenerForFrameMenu(frame);
    }

    protected _bindListenerForFrameMenu(frame?: IFrame): void {
        if (frame) {
            this._eventsController.addListener(frame.element, "openFrameMenuClicked", (event: IOpenFormMenuEvent) => this._openFrameMenuCallback(event), { options: { capture: true } });
        }
    }

    private async _openFrameMenuCallback(e: IOpenFormMenuEvent): Promise<void> {
        if (this._frameMenu) {
            if (this._frameMenu.isVisible()) {
                this._frameMenu.closeAll();
                this._frameMenu = undefined;
                return;
            }
        }
        if (this._frameMenuCancellationToken) {
            this._frameMenuCancellationToken.cancel();
        }
        this._frameMenuCancellationToken = new CancellationTokenSource();

        this._frameMenu = new DropDownMenu();

        for (const action of this._frameMenuActions) {
            const standartActionDefinition = FormUIStandard.standardActionDefinitions.get(action.actionType);
            if (!standartActionDefinition) {
                logger.error(`Action of type ${action.actionType} does not exists in standardActionDefinitions.`);
                continue;
            } else if (action.actionType === ActionType.standardGraph) {
                await action.action(this._frameMenuCancellationToken.token);
            } else {
                let item: DropDownMenuItem;
                if (action instanceof ITwoStateAction) {
                    item = DropDownMenuTwoStateItem.createDropTwoStateMenuItem(action, standartActionDefinition.icon?.toString(), action.action, standartActionDefinition.locKey || action.locKey,
                        true, standartActionDefinition.priority, undefined);
                } else {
                    item = DropDownMenuItem.createDropMenuItem(standartActionDefinition.icon?.toString(), action.action, standartActionDefinition.locKey || action.locKey,
                        true, standartActionDefinition.priority, undefined);
                }
                this._frameMenu.addItem(item);
            }
        }

        this._frameMenu.show(e.target as HTMLElement, e.detail);
    }

    /**
     * Aktualizuje počítadlo změněných záznamů, které se zobrazuje na tlačítku "uložit změny".
     */
    private _updateChangedRecordsCounter(count: number): void {
        if (this._btnSaveChanges) {
            this._btnSaveChanges.element.classList.toggle("primary", count > 0); //nastaví btn-primary pokud jsou změněné některé záznmy.
            const counterEl = this._btnSaveChanges.element.querySelector(".counter");
            if (counterEl) {
                if (count > 0) counterEl.textContent = count.toString();
                else counterEl.textContent = "";
            }
        }
    }

    /**
     * Upraví styl tlačítka "..." na řádku se změněným záznamem
     */
    private _updateChangedRow(row: Element, isChanged: boolean): void {
        if (!row)
            return;
        const actionBtn = row.querySelector(".actions");
        if (actionBtn) {
            actionBtn.classList.toggle("primary", isChanged);
            actionBtn.classList.toggle("flat", !isChanged);
        }
    }

    /**
     * Přidání akce do toolbaru
     * @param icon Ikonka, která se zobrazuje na tlačítku akce
     * @param menuType Do jakého menu se má akce zařadit
     * @param action Objekt reprezentující akci
     */
    public addAction<T extends IAction>(action: T): T {
        if (this._hideToolbar && !(action instanceof NodeAction) && !(action instanceof StandardAction)) { // pokud pridavame do skryteho toolbaru nejake polozky upozornime toho kdo to tam pridava, aby si to osetril :)
            logger.error(`Toolbar of frame ${this._frame.frameName} is hidden (most likely due to property HideToolbar) but action of type '${action.constructor.name}' and id '${action.id}' is being added. Check your form definition and remove it.`);
        }

        if (!FormUIStandard.customizeAction(action)) //akce není definována, nebude použita
            return null;

        this._allActions.add(action);

        if (action.actionType === ActionType.standardFulltextSearch && this._fulltextContainer) {
            const fulltextControl = new FulltextSearchControl();
            fulltextControl.addAction(action);
            this._fulltextContainer.appendChild(fulltextControl.element);
            return action;
        }

        if (action instanceof NodeAction) {
            if (action.toMenu === MenuType.mtFrameMenu) {
                this._createGraphFrameMenuFromNodeAction(action);
            } else {
                action.actions.forEach((node) => FormUIStandard.customizeAction(node));

                this._addObjectAction(MenuTypes.toolBar, action, true); //add objectAction musí být před addFrameAction. Ten totiž mění property MasterNode a přidává tam id HTML komponent.
                return this._addFrameAction(MenuTypes.toolBar, action);
            }
        } else if (action.toMenu === MenuType.mtFrameMenu) {
            this._frameMenuActions.add(action);

            return action;
        }

        return this._addActionByContext(action);
    }

    protected _createGraphFrameMenuFromNodeAction(nodeAction: NodeAction): void {
        const submenu = new DropDownMenu();
        for (const subAction of nodeAction.actions) {
            if (!(subAction instanceof ActionWithData)) {
                submenu.addItem(DropDownMenuItem.createDropMenuItem(subAction.icon ? subAction.icon.toString() : "", subAction.action, subAction.locKey, subAction.enabled, subAction.priority));
            } else {
                if (subAction.data != null && subAction.data instanceof IGraphDataForAction) {
                    const item = new DropDownMenuItemWithButton();
                    item.action = subAction;

                    item.addButton({
                        locKey: "GUI-675418", //Smazat graf
                        displayOn: DropDownMenuButtonDisplayOn.hover,
                        action: async (item) => {
                            item.parent.closeAll();
                            const qm = new MessageWindow(new LocalizedStringBuilder("GUI-599180")); // Odstranit?
                            qm.okButtonCaption = "GUI-146838"; // Ano
                            qm.noButtonCaption = "GUI-146840"; // Ne
                            qm.type = MessageType.message;
                            const r = await qm.show();
                            if (r.result == MessageButtonType.ok) {
                                const profile = await OWManager.getObject(AttrValueReader.read(subAction.data.chartProfileId), "UserProfile");
                                profile.delete();
                                item.remove();
                            }
                        },
                        icon: ["remove", "editorial-trash"],
                    });

                    item.setCaption(subAction.data.chartName);
                    submenu.addItem(item);
                }
            }
        }
        if (!this._frameMenu) {
            this._frameMenu = new DropDownMenu();
        }
        this._frameMenu.addItem(DropDownMenuWithSubmenuItem.createDropSubMenuItem(nodeAction.icon, nodeAction.locKey, submenu, nodeAction.priority, nodeAction.toMenu));
    }

    // v ideálním případě tuto signaturu neměnit, používají ji vlastní implementace FormUI
    protected _addActionByContext<T extends IAction>(action: T): T {
        if (action.context === ActionContext.frameAction) {
            return this._addFrameAction(MenuTypes.toolBar, action);
        } else {
            if (action instanceof XGAction && EnumUtils.hasFlag(action.supportedSelectionModes, SelectionType.multi)) {
                // Akce se vyskytuje jak u záznamu, tak v toolbaru. Kopie pro toolbar pro případ více označených záznamů
                const multiAction = new MultiObjectAction(action);

                this._storeMultiObjectAction(action, multiAction);
                this._allActions.add(multiAction);
                this._addFrameAction(MenuTypes.toolBar, multiAction);
            }

            return this._addObjectAction(MenuTypes.toolBar, action);
        }
    }

    protected _addObjectAction<T extends IAction>(_menuType: MenuTypes, action: T, isAlsoFrameAction: boolean = false): T {
        if (!isAlsoFrameAction && action instanceof StandardAction) {
            //pokud se jedná jen o akci objektu, tak se zkontroluje, aby se nepřidala 2x.
            const existingAction = this._getAction(action.actionType, this._getObjectAndFrameActionsIterator());
            if (existingAction != null) {
                return existingAction as T;
            } else {
                this._objectActions.add(action);
            }
        }

        this._objectActionsMenu.add(action);
        return action;
    }

    protected _addFrameAction<T extends IAction>(menuType: MenuTypes, action: T): T {
        if (action instanceof StandardAction) {
            const a = this._getAction(action.actionType, this._getObjectAndFrameActionsIterator());
            if (a != null)
                return a as T;
        }
        this._frameActions.add(action);

        if (action instanceof NodeAction && action.nodeActionType == NodeActionType.naGroup)
            action.masterNode = this._actionContainer.ctrlID;

        //Pokud je nova akce zalozka tak ji rovnou pridam do toolbaru
        if (action instanceof NodeAction && action.nodeActionType == NodeActionType.naPage) {
            action.nodeActionType = NodeActionType.naGroup;
            action.masterNode = this._actionContainer.ctrlID;
        }

        if (!action.masterNode) {
            action.masterNode = (action.toMenu !== undefined ? MenuType[action.toMenu] : "mtNone") + this._actionContainer.ctrlID;
        }

        if (action.masterNode) { //pokud ma masternode
            const masterNodes: GenericControl[] = this.findMasterNode(action.masterNode);

            if (masterNodes.length > 0) { //pokud nasel masternode
                const masterNode: GenericControl = masterNodes[0];

                if (!this._addActionToMasterNode(action, masterNode, menuType))
                    return action;

                //ToolbarButton pro uložit změny, má být v editaci speciálně nastylován
                if (action instanceof StandardAction && action.actionType === ActionType.standardSaveAll) {
                    action.toolbarButton.element.classList.add("save-changes");
                    action.toolbarButton.element.appendChild(createElement({ type: "span", classes: "counter" }));
                    this._btnSaveChanges = action.toolbarButton;
                    this._updateChangedRecordsCounter(0);
                }

                //pridal jsem nodeaction proto se pokusim pridat dosud nezarazene akce
                if (action instanceof NodeAction)
                    this._tryAddActionFromWaitingStack();
            } else {
                this._frameActionsWaitingToAdd.push(new ToolbarActionPrescription(menuType, action));
                return action;
            }
        } else {
            logger.error("Action id '" + action.id + "' of type " + ActionType[action.actionType] + " has unknow masterNode and will be skipped");
        }

        this._updateVisibility();
        action.visibilityChanged.add(() => this._updateVisibility());

        action.toolbarButton?.visibilityChanged?.add(() => {
            if (this._actionContainer) {
                this._actionContainer?.groups?.throttledResizeGroups();
            }
        });

        if (this._actionContainer) {
            TimerUtils.intervalOrCancel(`toolbarGroupResize${this._actionContainer.ctrlID}`, //`
                () => {
                    return this._actionContainer?.groups?.resizeGroups();
                }, 300, 20);
        }

        return action;
    }

    protected _addActionToMasterNode<T extends IAction>(action: T, masterNode: GenericControl, menuType: MenuTypes): boolean {
        const icon = action.icon;
        const classes = MenuUIStandard._getClasses(action);

        if (masterNode instanceof ActionsContainer) { //masternode je zalozka
            if (action instanceof NodeAction && (action as NodeAction).nodeActionType == NodeActionType.naGroup) {
                masterNode.addOrFindGroup(action.locKey, action.toMenu, action.ctrlID, action.priority);
            } else {
                //MasterNode je "nedefaultni" zalocka, proto menim MasterNode na mtNone a pokusim se akci znovu vlozit
                if (action.toMenu)
                    action.masterNode = MenuType[action.toMenu] + masterNode.ctrlID;
                else
                    action.masterNode = "mtNone" + masterNode.ctrlID;
                this._addFrameAction(menuType, action);
                return false;
            }
        }
        if (masterNode instanceof ToolbarGroup) { //masternode je skupina
            FormUIStandard.createToolbarButton(action, icon, menuType, classes, this._getLanguages);
            masterNode.addAction(action);
        }
        if (masterNode instanceof IAction) { //masternode je uzel
            FormUIStandard.createToolbarButton(action, icon, MenuTypes.contextMenu, classes, this._getLanguages);
            (masterNode as NodeAction).addAction(action);
        }

        return true;
    }

    /**
     * Přidá MultiObjectAction do vnitřního úložiště
     * @param action Akce, ke které se vytvořila MultiObjectAction
     * @param multiObjectAction Vytvořená MultiObjectAction
     */
    protected _storeMultiObjectAction(action: IAction, multiObjectAction: IMultiObjectAction): void {
        let multiObjectActionSet: Set<IMultiObjectAction>;

        if (!this._multiObjectActionsMap.has(action)) {
            multiObjectActionSet = new Set<IMultiObjectAction>();
            this._multiObjectActionsMap.set(action, multiObjectActionSet);
        } else {
            multiObjectActionSet = this._multiObjectActionsMap.get(action);
        }

        multiObjectActionSet.add(multiObjectAction);
    }

    /**
     * Pokusí se odstranit MultiObject akce odvozené od předané akce
     * @param action Akce jejíž odvozené akce se mají odstranit
     * @returns Vrací true, pokud byly odvozené MultiObject akce odstraněny. Jinak false
     */
    protected _tryDropMultiObjectActionsForAction(action: IAction): boolean {
        if (this._multiObjectActionsMap.has(action)) {
            const multiObjectActionSet = this._multiObjectActionsMap.get(action);
            for (const multiObjectAction of multiObjectActionSet) {
                this._allActions.delete(multiObjectAction);
            }

            this._multiObjectActionsMap.delete(action);

            return true;
        }

        return false;
    }

    protected _hasVisibleActions(): boolean {
        if (!this._hideToolbar) {
            return this._hasIterableAnyVisibleActions(this._getObjectAndFrameActionsIterator());
        }

        return false;
    }

    private _hasIterableAnyVisibleActions<IAction>(iterable: Iterable<IAction>): boolean {
        for (const action of iterable) {
            if (action instanceof IAction && action.visible) {
                return true;
            }
        }

        return false;
    }

    /**
     * Vytvori defaulni skupiny mtRecord, mtFormControl atd.
     */
    public createDefaultGroups(): void {
        const pageName = this._actionContainer.ctrlID;
        for (const toMenu in MenuType) {
            const isValueProperty = parseInt(toMenu, 10) >= 0;
            if (isValueProperty) {
                const menuName = MenuType[toMenu];
                const menuType = EnumUtils.getEnumValueFromName(MenuType, menuName);
                this._createGroup(menuType, menuName + pageName, pageName);
            }
        }
    }

    /**
     * Vytvareni vlastnich group, ktere nejsou z definice formulare
     */
    private _createGroup(toMenu: MenuType, nodeName: string, masterNode: string): void {
        const doc = document.createElement("div");
        doc.setAttribute("data-NodeActionType", "naGroup");
        doc.setAttribute("data-ctrlid", nodeName);
        const node = new NodeAction({ element: doc, originalRightsDefinition: EMPTY_ITEM_RIGHTS_COLLECTION });
        node.toMenu = toMenu;
        node.priority = this._getGroupPriority(toMenu);
        node.masterNode = masterNode;
        this.addAction(node);
    }

    private _getGroupPriority(toMenu: MenuType): number {
        switch (toMenu) {
            case MenuType.mtRecordNavigation:
                return -120;
            case MenuType.mtFormControl:
                return -100;
            case MenuType.mtRecord:
                return -80;
            case MenuType.mtActions:
                return 110;
            case MenuType.mtForm:
                return 120;
            case MenuType.mtWorkflow:
                return 130;
            case MenuType.mtNone:
                return 140;
            case MenuType.mtPrint:
                return 150;
            case MenuType.mtFilter:
                return 160;
            case MenuType.mtCancelSave:
            case MenuType.mtStandardAction:
                return -90;
            default:
                return 50;
        }
    }

    /**
     * Prochazi akce, ktere maji masterNode a ktere v prvnim kole pridavani do toolbaru nenasli svuj MasterNode
     * A pokud jiz jejich masterNode v toolbaru je tak jsou do toolabru umisteny
     */
    private _tryAddActionFromWaitingStack(): void {
        for (let i = 0; i < this._frameActionsWaitingToAdd.length; i++) {
            const aktAction = this._frameActionsWaitingToAdd[i];
            //Pokud je
            if (this.findMasterNode(aktAction.action.masterNode).length > 0) {
                this._frameActionsWaitingToAdd.splice(i, 1);
                this._addFrameAction(aktAction.menuType, aktAction.action);
                this._tryAddActionFromWaitingStack();
                return;
            }
        }
    }

    protected static _getClasses(action: IAction): string[] {
        const def = FormUIStandard.standardActionDefinitions.get(action.actionType);
        return def ? def.classes : [];
    }

    /**Vrati masternode podle definice*/
    public findMasterNode(ctrlID: string): GenericControl[] {
        return this._actionContainer.findMasterNode(ctrlID);
    }

    /**
     * Odstraní zaregistrovanou akci z menu
     * @param action Akce k odstranění
     */
    public removeAction(action: IAction): void {
        if (action.masterNode) { //pokud ma masternode
            const masterNodes: GenericControl[] = this.findMasterNode(action.masterNode);

            if (masterNodes.length > 0) { //pokud nasel masternode
                const control: GenericControl = masterNodes[0];

                if (control instanceof ToolbarGroup) { //masternode je skupina
                    control.removeAction(action);
                }
                if (control instanceof IAction) { //masternode je uzel
                    (control as NodeAction).removeAction(action);
                }
                //pridal jsem nodeaction proto se pokusim pridat dosud nezarazene akce
                if (action instanceof NodeAction)
                    this._tryAddActionFromWaitingStack();
            }
        }

        if (action.toolbarButton) {
            action.toolbarButton.dropDownMenuItem.remove();
            action.toolbarButton.element.remove();
        }

        if (action.context == ActionContext.frameAction || action instanceof NodeAction) {
            if (this._frameActions.has(action)) {
                this._frameActions.delete(action);
            }
        } else if (action.context == ActionContext.objectAction || action instanceof NodeAction) {
            this._objectActionsMenu.remove(action);
            this._objectActions.delete(action);
        }

        if (action.toMenu === MenuType.mtFrameMenu) {
            this._frameMenuActions.delete(action);
        }

        this._tryDropMultiObjectActionsForAction(action);

        if (this._allActions.has(action)) {
            this._allActions.delete(action);
        }
    }

    private _getAction<T extends IAction>(actionType: ActionType, actions: Iterable<IAction> = this._allActions): T {
        return this._getIterator<T>(actions, this._actionTypeComparator(actionType)).next().value;
    }

    private _getActions<T extends IAction>(actionType: ActionType, actions: Iterable<IAction> = this._allActions): Iterable<T> {
        return this._getIterator<T>(actions, this._actionTypeComparator(actionType));
    }

    /**
     * Vrací akci podle jejího typu, pokud jich je víc, tak vrátí první
     * @param actionType Typ akce
     */
    public getAction<T extends IAction>(actionType: ActionType): T {
        return this._getAction(actionType);
    }

    /**
     * Vrací akce podle typu
     * @param actionType Typ akce
     */
    public getActions<T extends IAction>(actionType: ActionType): Iterable<T> {
        return this._getActions(actionType);
    }

    /**
    * Vrací akce podle použitého kontextu
    * @param actionContext Kontext akce
    */
    public getActionsByContext<T extends IAction>(actionContext?: ActionContext): Iterable<T> {
        if (actionContext === ActionContext.objectAction)
            return this._objectActionsMenu.getActions<T>();
        else
            return this._getIterator<T>(this._allActions, this._actionContextComparator(actionContext));
    }

    /**
     * Vrací iterátor s nalezenými akcemi
     * @param actions Seznam akcí k prohledání
     * @param actionType Typ hledané akce
     */
    private *_getIterator<T extends IAction>(actions: Iterable<IAction>, comparator?: (action: IAction) => boolean): IterableIterator<T> {
        for (const action of actions) {
            if (comparator == null || comparator(action)) {
                yield action as T;
            }

            if (action instanceof NodeAction && action.actions.length > 0) { // Je potřeba projít i všechny vnořené akce
                yield* this._getIterator<T>(new Set<IAction>(action.actions), comparator);
            }
        }
    }

    /**
     * Nastavení požadované viditelnosti toolbaru.
     * Kontroluje i nastavení hideToolbar a viditelnost akcí.
     */
    public setVisibility(visibility: boolean): void {
        if (this.checkAndLogDisposedUsage("setVisibility")) return;

        this._outerVisibility = visibility;
        this._updateVisibility();
    }

    /**
     * Zkontroluje viditelnost jednotlivých akcí a podle nich nastaví viditelnost toolbaru.
     * Bere v ptaz i hideToolbar a viditelnost nastavenou z vnějšku komponenty.
     */
    protected _updateVisibility(): void {
        if (this._hideToolbar)
            return;

        // toolbar se zobrazuje jen pokud má nějakou viditelnou akci
        this._setVisibility(this._outerVisibility && this._hasVisibleActions());
    }

    /**
     * Nastaví viditelnost. Neprovádí žádné další kontroly.
     * @param visibility Požadovaná viditelnost
     */
    protected _setVisibility(visibility: boolean): void {
        if (this.isVisible == visibility)
            return;

        this.isVisible = visibility;
        this.element.classList.toggle("hiddenToolbar", !this.isVisible);
        this._loaderElement.classList.toggle("hiddenLoader", !this.isVisible);
    }

    /**
     * Umožňuje nastavit vlastní specifické property, na které mohou různé implementace reagovat
     * @param propertyName Název property - buď enum FormUICustomProperty nebo vlastní libovolný string
     * @param newValue Nastavovaná hodnota
     */
    public setProperty(propertyName: FormUICustomProperty | string, newValue: any): void {
        switch (propertyName) {
            case MenuUIExtensionProperties.fulltextSupported:
                this._fulltextSupported = newValue;
                this._createFulltextContainerOrSetVisibility(newValue);
                break;
            case MenuUIExtensionProperties.filterContainer:
                this.setFilterContainter(newValue);
                break;
            case FormUICustomProperty.notificationDot:
                if (newValue) {
                    const val: {
                        action: ActionType;
                        lockey?: string;
                        visibility: boolean;
                    } = newValue;

                    const action = this.getAction(val.action);
                    if (action && action.toolbarButton) {
                        action.toolbarButton.changeVisibilityNotificationDot(val.visibility, val.lockey);
                    }
                }
                break;
            default:
        }
    }

    /**
     * Přidá standardní akci pro řádek záznamu. Tato akce je sdílená mezi řádky. Pokud již tato akce existuje, vrací undefined, jinak vrací nově vytvořenou akci.
     * @param enabled
     * @protected
     */
    protected _addCustomRecordActions(enabled: boolean = true): IAction | undefined {
        //přidat akci ...
        if (!this._hideToolbar) {
            const existingAction = this._getAction(ActionType.standardRowActions, this._getObjectAndFrameActionsIterator());
            if (existingAction) {
                return undefined;
            }

            const actionShowRecordMenu = FrameSharedMethods.createStandardRecordAction(ActionType.standardRowActions, (e: Event, dataKey: string) => {
                return this._showRowActionMenu(e, dataKey);
            }, this._frame);
            actionShowRecordMenu.enabled = enabled;
            actionShowRecordMenu.visible = true;

            this.addAction(actionShowRecordMenu);
            return actionShowRecordMenu;
        } else {
            return undefined;
        }
    }

    /**
     * Vytvoří element s ovládacími prvky, které se použijí v kontextu datového záznamu
     */
    public renderRecordControls(dataKeyFetcher: () => string): HTMLElement {
        if (this._frame instanceof IMasterableFrameWithDataGrid && this._frame.renderStandardRowActions) {
            const frame = this._frame; //musí se vytahnout kvůli arrow funkci na následujícím řádku
            this._addCustomRecordActions(frame.currentDataGrid.interactive);
        }

        const container = createElement({ classes: "record-controls-container" });
        const eventController = new EventsController();
        for (const a of this._objectActionsMenu.getActions()) {
            if (!(a instanceof StandardRecordAction) ||
                a.actionType === ActionType.standardCancelRecordChanges) //Tlačítko "zrušit změny" pro ui-standart bude renderováno pouze v drop down menu.
                continue;

            this._initRecordControl(a, eventController);

            const def = FormUIStandard.standardActionDefinitions.get(a.actionType);
            const iconDef = def.icon;
            const icon: string = typeof iconDef === "number" ? Icon[iconDef] : iconDef;
            const el = createElement({ type: "button", classes: ["record-control-cell", "btn", icon, ...(def.classes ? def.classes : [])] });

            if (a.extendedProperties)
                setExtendedPropertiesOnElement(a.extendedProperties, this.element);

            if (!a.visible)
                el.style.display = "none";

            eventController.add(a.visibilityChanged, (_sender, visible) => {
                el.style.display = visible ? "" : "none";

                if (this._frame instanceof IMasterableFrameWithDataGrid && this._frame.currentDataGrid instanceof IListDataGrid) {
                    this._frame.currentDataGrid.resizeHeaderFirstColumn();
                }
            });

            eventController.add(a.enabledChanged, (_sender, value) => {
                el.classList.toggle("Disabled", !value);
            });

            eventController.add(a.isActiveChanged, (_sender, value) => {
                el.classList.toggle("Disabled", !value);
            });

            const captionElement = new BtnCaption();
            eventController.addListener(captionElement, "titleChanged", () => {
                container.dispatchEvent(new CustomEvent("widthChanged", {}));
            });

            const lockey = def.locKey ? def.locKey : a.locKey;
            if (lockey) {
                Localization.addLocData(captionElement, lockey);
            }
            el.appendChild(captionElement);

            Localization.addLocTxtData(el, a.locKey, "title");
            eventController.addListener(el, "click", (e) => {
                if (a.enabled && a.interactive)
                    a.recordAction(e, dataKeyFetcher());
            });
            eventController.addListener(el, "keyup", (e) => {
                if (e.keyCode == 13) e.stopPropagation();
            });
            el.classList.toggle("Disabled", (!(a.enabled && a.interactive) || !this._frame.interactive));

            container.appendChild(el);
        }

        const disposableContainer = container as IHtmlElementWithResources;
        disposableContainer.dispose = () => {
            eventController.dispose();
        };
        return disposableContainer;
    }

    protected _initRecordControl(action: IAction, eventsController: EventsController): void {
        switch (action.actionType) {
            case ActionType.standardSelectRecord:
                //zakazani tlacitka select, na zaklade otevreni modalniho okna
                if (this._frame instanceof IMasterableFrame && this._frame.ownerForm && this._frame.ownerForm.enableLovSelect) {
                    let childOpened = 0;

                    eventsController.add(this._frame.ownerForm.childClosed, () => {
                        if (--childOpened == 0)
                            action.enabled = this._frame.actualObject != null;
                    });

                    eventsController.add(this._frame.ownerForm.childOpened, () => {
                        childOpened++;
                        action.enabled = false;
                    });
                }
                break;

            default:
                break;
        }
    }
    /*
     * Vrátí element ke kterému má být přilepeno otevíráné kontextové menu,
     * V obecném případě podle dataKey najde datarow a na něm element reprezentující "..." akci
     */
    protected _getContextMenuAnchorElement(dataKey: string, row?: IDataRowElement, _e?: Event): HTMLElement {
        if (!row)
            row = (this._frame as IMasterableFrameWithDataGrid).currentDataGrid.dataRows.find(r => r.dataKey == dataKey);
        if (!row)
            throw new InvalidOperationException(`Row with dataKey ${dataKey} not found`);

        return row.querySelector(".recordActions") as HTMLElement || row;
    }
    /**
     * Zobrazí dropdown menu pro akce s objektem.
     */
    protected async _showRowActionMenu(e: Event, dataKey: string): Promise<void> {
        if (this._frame instanceof IMasterableFrameWithDataGrid) {
            const row = this._frame.currentDataGrid.dataRows.find(r => r.dataKey == dataKey);

            if (!row)
                throw new InvalidOperationException(`Row with dataKey ${dataKey} not found`); // `
            //pokud je už záznam vybraný, může se menu zobrazit ihned. Jinak se spustí výběr příslušného záznamu s loaderem a menu se zobrazí až je objekt načten a práva vyhodnocena.
            let isRowSelected = row.dataKey.toString() === this._frame.actualObject?.id.toString();
            if (this._frame instanceof IObjectListEdit) {
                isRowSelected = row.isSelected;
            } else if (this._frame instanceof IDetailMasterableFrame && this._frame.detail)
                isRowSelected = isRowSelected && this._frame.detail.actualObject?.id.toString() === row.dataKey.toString();
            else if (this._frame instanceof CalendarFrame) {
                const complexKey = row.dataKey.split(CalendarDataRow.dataKeySplitterChar);
                isRowSelected = complexKey.length > 1
                    && this._frame.currentDataGrid.uniqueDSId === complexKey[0]
                    && this._frame.actualObject?.id.toString() === complexKey[1];
            }
            if (isRowSelected && this._frame.rightsManager.runningEvaluationsCount === 0 && !this._frame.isSetActualObjectRunning) //v případě, že je označeno více záznamů, může být actualobject==null, pak se provede vyběr řádku a zruší označené záznamy.
                this._openThreeDotsOnSelectedRow(dataKey, row, e);
            else {
                this._openThreeDotsWithCheckChanges(dataKey, row, e);
            }
        }
    }
    /**
     * Otevře menu akcí objektu nad aktualním objektem.
     */
    protected _openThreeDotsOnSelectedRow(dataKey: string, row?: IDataRowElement, e?: Event, forceRedraw?: boolean): void {
        const anchorElement = this._getContextMenuAnchorElement(dataKey, row, e);

        if (forceRedraw || !this._objectActionsMenu.isShown) {
            this._objectActionsMenu.close();
            this._objectActionsMenu.show(anchorElement, e, this._frame.actualObject, this._itemsInEditMode.has(dataKey), this._frame);
        } else {
            this._objectActionsMenu.close();
        }
    }

    /**
     * Otevře menu akcí objektu spolu se změnou na jiný objekt.
    */
    protected _openThreeDotsWithCheckChanges(dataKey: string, row?: IDataRowElement, e?: Event): void {
        const anchorElement = this._getContextMenuAnchorElement(dataKey, row, e);

        row.selectWithCheckChanges(false);
        this._pendingRightsEvaluationObjId = dataKey;
        this._objectActionsMenu.showEmpty(anchorElement, e);
    }

    private _createFulltextContainerOrSetVisibility(value: boolean): void {
        if (!this._fulltextContainer && value) {
            this._fulltextContainer = createElement({ type: "div", classes: "fulltext-container" }) as HTMLDivElement;
            this.element.appendChild(this._fulltextContainer);
        } else if (this._fulltextContainer)
            this._fulltextContainer.hidden = !value;
    }

    public setFilterContainter(content: HTMLElement[]): void {
        if (content) {
            this._filterContainer.innerHTML = "";
            for (const element of content)
                this._filterContainer.appendChild(element);
            this._filterContainer.classList.toggle("visible", true);
        } else {
            this._filterContainer.classList.toggle("visible", false);
        }
    }

    /**
     * Zkontroluje, zda je řádek disposed a pokud ano zaloguje warning o volání metody nad disposed řádkem.
     *
     * Tato metoda a obecně kontroly na dispose by neměly být nutné po odstranění příčin, kvůli kterým se pracuje i nadále s disposed objekty.
     * Pro řešení příčin je vytvořený task 33553.
     *
     * @param methodName Název metody, kde se provádí kontrola. Slouží pro logování.
     * @return true, pokud je řádek disposed, jinak false.
     */
    protected checkAndLogDisposedUsage(methodName: string): boolean {
        const disposed = this.isDisposed();
        if (disposed) {
            logger.warn(`Calling ${methodName} on disposed ${this.constructor.name}!`);
        }
        return disposed;
    }

    public isDisposed(): boolean {
        return this._isDisposed;
    }

    public dispose(): void {
        if (this._isDisposed) {
            return;
        }

        UIManager.instance.resizeUnobserve(this._frameElement, this._actionContainerResizeHandler);
        this._eventsController?.dispose();
        this._objectActionsMenu?.dispose();

        this._frameActionsWaitingToAdd = [];
        this._objectActionsWaitingToAdd = [];
        this._actionContainer?.dispose();

        this._allActions?.forEach(action => action.dispose());
        this._allActions.clear();
        this._objectActions?.forEach(action => action.dispose());
        this._objectActions.clear();
        this._frameActions?.forEach(action => action.dispose());
        this._frameActions.clear();
        this._frameMenuActions?.forEach(action => action.dispose());
        this._frameMenuActions.clear();
        this._multiObjectActionsMap.clear();

        this._btnSaveChanges?.dispose();
        this._itemsInEditMode.clear();
        this._frameMenu?.dispose();
        this._frameMenuCancellationToken?.cancel();

        this._isDisposed = true;
    }
}

export class MenuUIStandardDetail extends MenuUIStandard {
    /**
     * Přidání akce do toolbaru
     * @param icon Ikonka, která se zobrazuje na tlačítku akce
     * @param menuType Do jakého menu se má akce zařadit
     * @param action Objekt reprezentující akci
     */
    public override addAction<T extends IAction>(action: T): T {
        if (action.actionType === ActionType.standardRowActions || !FormUIStandard.customizeAction(action))
            return null;

        if (action instanceof NodeAction) {
            action.actions.forEach((node) => FormUIStandard.customizeAction(node));
        }

        //v tomto případě se nerozlišuje, jestli jde o akci objektu nebo frame. Proto je potřeba ošetřit aby se akce "uložit" a "zrušit změny", nepřidali pro frame i pro záznam - docházelo ke zdvojení.
        if (action.actionType !== ActionType.standardSaveRecord
            && action.actionType !== ActionType.standardCancelRecordChanges) {
            this._allActions.add(action);
            return this._addFrameAction(MenuTypes.toolBar, action);
        }

        return null;
    }

    // Přepsané protože MenuUIStandartDetail nemá FrameMenu, proto nepotřebuje listener
    protected override _bindListenerForFrameMenu(_frame?: IFrame): void {
        // Prázdná metoda
    }
}
